/**
 * x-quantity directive.
 *
 * Manages a quantity input, providing reactive increment and decrement actions
 * that respect the input's `min`, `max`, and `step` attributes. Initial values
 * are read directly from the `[x-ref="quantityInput"]` element.
 *
 * @example
 *   <div x-quantity>
 *     <button @click="decrement">−</button>
 *     <input x-ref="quantityInput" type="number" :value="value" min="1" max="10" step="1">
 *     <button @click="increment">+</button>
 *   </div>
 */
(() => {
  const root = (Alpine, el) => {
    const input = el.querySelector('[x-ref="quantityInput"]');

    const min = parseFloat(input.min) || 1;
    const max = parseFloat(input.max) || 9999;
    const step = parseFloat(input.step) || 1;
    const value = parseFloat(input.value) || min;

    Alpine.bind(el, () => ({
      ["x-data"]() {
        return {
          value,
          min,
          max,
          step,

          /**
           * Increases the quantity by one step, capped at the maximum.
           */
          increment() {
            this.value = Math.min(this.max, this.value + this.step);
          },

          /**
           * Decreases the quantity by one step, floored at the minimum.
           */
          decrement() {
            this.value = Math.max(this.min, this.value - this.step);
          },

          /**
           * Sets the current quantity value.
           * @param {number} value - The new quantity.
           */
          set quantityValue(value) {
            this.value = value;
          },

          /**
           * Returns the current quantity value.
           * @returns {number} The current quantity.
           */
          get quantityValue() {
            return this.value;
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("quantity", (el, { expression }, { evaluate }) => {
      const options = expression ? evaluate(expression) : {};

      root(Alpine, el, options);
    }).before("bind");
  });
})();
